import { ReactNode } from 'react';

interface ChipOption {
  id: string;
  label: string;
  icon?: ReactNode;
  description?: string;
  disabled?: boolean;
}

interface ChipSelectorProps {
  label?: string;
  options: ChipOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  multiSelect?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outline' | 'solid';
  className?: string;
}

export function ChipSelector({
  label,
  options,
  selected,
  onChange,
  multiSelect = true,
  size = 'md',
  variant = 'default',
  className = '',
}: ChipSelectorProps) {
  const handleSelect = (optionId: string) => {
    if (multiSelect) {
      if (selected.includes(optionId)) {
        onChange(selected.filter((id) => id !== optionId));
      } else {
        onChange([...selected, optionId]);
      }
    } else {
      onChange(selected.includes(optionId) ? [] : [optionId]);
    }
  };

  const sizeClasses = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-1.5',
    lg: 'text-base px-4 py-2',
  };

  const getChipClass = (isSelected: boolean, isDisabled: boolean) => {
    const baseClass = `inline-flex items-center gap-1.5 rounded-full font-medium transition-all cursor-pointer select-none ${sizeClasses[size]}`;

    if (isDisabled) {
      return `${baseClass} bg-surface-700 text-text-tertiary cursor-not-allowed opacity-50`;
    }

    if (variant === 'solid') {
      return isSelected
        ? `${baseClass} bg-primary-500 text-white shadow-md`
        : `${baseClass} bg-surface-600 text-text-secondary hover:bg-surface-500`;
    }

    if (variant === 'outline') {
      return isSelected
        ? `${baseClass} border-2 border-primary-500 text-primary-400 bg-primary-500/10`
        : `${baseClass} border border-surface-400/30 text-text-secondary hover:border-gray-500 hover:text-text-secondary`;
    }

    // Default variant
    return isSelected
      ? `${baseClass} bg-primary-500/20 text-primary-300 border border-primary-500/40`
      : `${baseClass} bg-surface-600/50 text-text-secondary border border-surface-400/20 hover:bg-surface-600 hover:text-text-secondary`;
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-text-secondary mb-2">{label}</label>
      )}
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const isSelected = selected.includes(option.id);
          const isDisabled = option.disabled || false;

          return (
            <button
              key={option.id}
              type="button"
              onClick={() => !isDisabled && handleSelect(option.id)}
              className={getChipClass(isSelected, isDisabled)}
              disabled={isDisabled}
              title={option.description}
            >
              {isSelected && (
                <CheckIcon className="w-3.5 h-3.5" />
              )}
              {option.icon}
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Simple tag list for display only (non-interactive)
export function TagList({
  tags,
  size = 'sm',
  variant = 'default',
  maxVisible,
  className = '',
}: {
  tags: string[];
  size?: 'xs' | 'sm' | 'md';
  variant?: 'default' | 'outline' | 'colored';
  maxVisible?: number;
  className?: string;
}) {
  const sizeClasses = {
    xs: 'text-xs px-1.5 py-0.5',
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-2.5 py-1',
  };

  const variantClasses = {
    default: 'bg-surface-600 text-text-secondary',
    outline: 'border border-surface-400/30 text-text-secondary',
    colored: 'bg-primary-500/15 text-primary-300',
  };

  const visibleTags = maxVisible ? tags.slice(0, maxVisible) : tags;
  const hiddenCount = maxVisible ? Math.max(0, tags.length - maxVisible) : 0;

  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {visibleTags.map((tag, i) => (
        <span
          key={i}
          className={`rounded-full ${sizeClasses[size]} ${variantClasses[variant]}`}
        >
          {tag}
        </span>
      ))}
      {hiddenCount > 0 && (
        <span className={`rounded-full ${sizeClasses[size]} bg-surface-700 text-text-tertiary`}>
          +{hiddenCount}
        </span>
      )}
    </div>
  );
}

// Action card with icon - for action grids
export function ActionCard({
  icon,
  number,
  title,
  description,
  onClick,
  disabled,
  badge,
  className = '',
}: {
  icon: ReactNode;
  number?: number;
  title: string;
  description: string;
  onClick?: () => void;
  disabled?: boolean;
  badge?: string;
  className?: string;
}) {
  const Component = onClick ? 'button' : 'div';

  return (
    <Component
      onClick={onClick}
      disabled={disabled}
      className={`
        p-5 rounded-xl bg-surface-700 border border-surface-400/15 text-left
        transition-all group relative
        ${onClick && !disabled ? 'cursor-pointer hover:bg-surface-600 hover:border-primary-500/30' : ''}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${className}
      `}
    >
      {badge && (
        <span className="absolute top-3 right-3 text-xs px-2 py-0.5 bg-surface-600 text-text-secondary rounded">
          {badge}
        </span>
      )}

      <div className="flex items-center gap-3 mb-3">
        {number !== undefined && (
          <span className="w-6 h-6 rounded-full bg-primary-500/30 flex items-center justify-center text-xs font-bold text-primary-300">
            {number}
          </span>
        )}
        <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center text-xl">
          {icon}
        </div>
      </div>

      <h3 className="font-semibold text-white group-hover:text-primary-300 transition-colors mb-1">
        {title}
      </h3>
      <p className="text-sm text-text-secondary">{description}</p>
    </Component>
  );
}

function CheckIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}
